import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, User, Phone, ArrowRight, Shield, Eye, EyeOff, KeyRound } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useT } from '../context/LanguageContext';
import { requestLoginOtp } from '../lib/api';
import { Helmet } from 'react-helmet-async';

const formatMMSS = (sec) => {
  const s = Math.max(0, Math.floor(sec));
  const mm = String(Math.floor(s / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return `${mm}:${ss}`;
};

const PatientLoginPage = () => {
  const navigate = useNavigate();
  const t = useT();
  const { login, loginWithOtp, register, isLoggedIn, loading } = useAuth();

  // Redirect already-authenticated patients away from login page
  useEffect(() => {
    if (!loading && isLoggedIn) {
      navigate('/profile', { replace: true });
    }
  }, [isLoggedIn, loading, navigate]);

  const [mode, setMode] = useState('login'); // 'login' | 'register'
  const [loginMethod, setLoginMethod] = useState('password'); // 'password' | 'otp'
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Login state (shared email between password + OTP methods)
  const [step, setStep] = useState('email'); // 'email' | 'code'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [errors, setErrors] = useState({});
  const codeInputRef = useRef(null);
  const intervalRef = useRef(null);

  // Register-only state
  const [regForm, setRegForm] = useState({ name: '', email: '', phone: '', password: '' });

  // ─── Countdown ────────────────────────────────────────────────────────────
  const clearCountdown = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startCountdown = useCallback((seconds) => {
    clearCountdown();
    const initial = Math.max(0, Math.floor(seconds));
    setSecondsLeft(initial);
    if (initial <= 0) return;
    intervalRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearCountdown();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [clearCountdown]);

  useEffect(() => () => clearCountdown(), [clearCountdown]);

  // ─── Login (OTP) — step "email" ───────────────────────────────────────────
  const handleRequestOtp = async (e) => {
    e?.preventDefault?.();
    const trimmedEmail = email.trim();
    const newErrors = {};
    if (!trimmedEmail) newErrors.email = t('errors.required');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) newErrors.email = t('errors.invalidEmail');
    setErrors(newErrors);
    if (Object.keys(newErrors).length) return;

    setIsLoading(true);
    try {
      await requestLoginOtp(trimmedEmail);
      toast.success(t('patientAuth.codeSent'));
      setStep('code');
      setOtpCode('');
      startCountdown(60);
      // Focus the code input shortly after render
      setTimeout(() => codeInputRef.current?.focus(), 50);
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;
      if (status === 429) {
        const retry = Number(data?.retryAfterSeconds) || 60;
        toast.error(t('patientAuth.rateLimited', { seconds: retry }));
        setStep('code');
        setOtpCode('');
        startCountdown(retry);
        setTimeout(() => codeInputRef.current?.focus(), 50);
      } else {
        toast.error(data?.message || t('errors.network'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Login (OTP) — resend ─────────────────────────────────────────────────
  const handleResend = async () => {
    if (secondsLeft > 0 || isLoading) return;
    setIsLoading(true);
    try {
      await requestLoginOtp(email);
      toast.success(t('patientAuth.newCodeSent'));
      startCountdown(60);
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;
      if (status === 429) {
        const retry = Number(data?.retryAfterSeconds) || 60;
        toast.error(t('patientAuth.rateLimited', { seconds: retry }));
        startCountdown(retry);
      } else {
        toast.error(data?.message || t('errors.network'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Login (OTP) — step "code" ────────────────────────────────────────────
  const submitOtp = useCallback(async (code) => {
    if (code.length !== 6) {
      setErrors({ code: t('patientAuth.codeInvalid') });
      return;
    }
    setIsLoading(true);
    setErrors({});
    try {
      await loginWithOtp(email, code);
      toast.success(t('patientAuth.welcomeBack'));
      navigate('/profile');
    } catch (err) {
      const data = err?.response?.data;
      toast.error(data?.message || t('errors.network'));
      setOtpCode('');
      setTimeout(() => codeInputRef.current?.focus(), 30);
    } finally {
      setIsLoading(false);
    }
  }, [loginWithOtp, navigate, email, t]);

  const handleCodeChange = (raw) => {
    const digits = raw.replace(/\D/g, '').slice(0, 6);
    setOtpCode(digits);
    if (errors.code) setErrors({});
    if (digits.length === 6 && !isLoading) {
      // Auto-submit when 6 digits are entered
      submitOtp(digits);
    }
  };

  const handleVerifySubmit = (e) => {
    e.preventDefault();
    submitOtp(otpCode);
  };

  const handleChangeEmail = () => {
    setStep('email');
    setOtpCode('');
    setErrors({});
    clearCountdown();
    setSecondsLeft(0);
  };

  // ─── Login (password) ─────────────────────────────────────────────────────
  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    const trimmedEmail = email.trim();
    const newErrors = {};
    if (!trimmedEmail) newErrors.email = t('errors.required');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) newErrors.email = t('errors.invalidEmail');
    if (!password) newErrors.password = t('errors.required');
    setErrors(newErrors);
    if (Object.keys(newErrors).length) return;

    setIsLoading(true);
    try {
      await login(trimmedEmail, password);
      toast.success(t('patientAuth.welcomeBack'));
      navigate('/profile');
    } catch (err) {
      const msg = err?.response?.data?.message || t('errors.network');
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Login method switcher (password ⇄ otp) ───────────────────────────────
  const switchLoginMethod = (next) => {
    if (next === loginMethod) return;
    setErrors({});
    if (next === 'password') {
      // Leaving OTP — stop countdown + reset OTP state, keep email
      clearCountdown();
      setSecondsLeft(0);
      setStep('email');
      setOtpCode('');
    } else {
      // Leaving password — clear password, keep email
      setPassword('');
      setShowPassword(false);
      setStep('email');
      setOtpCode('');
    }
    setLoginMethod(next);
  };

  // ─── Register (unchanged behaviour) ───────────────────────────────────────
  const validateRegister = () => {
    const newErrors = {};
    if (!regForm.name.trim()) newErrors.name = 'Name is required';
    if (!regForm.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(regForm.email)) newErrors.email = 'Invalid email';
    if (!regForm.password) newErrors.password = 'Password is required';
    else if (regForm.password.length < 8) newErrors.password = 'Password must be at least 8 characters';
    if (regForm.phone && !/^[\d\s+()-]{8,20}$/.test(regForm.phone)) {
      newErrors.phone = 'Invalid phone number';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!validateRegister()) return;
    setIsLoading(true);
    try {
      await register(regForm.name, regForm.email, regForm.phone, regForm.password);
      toast.success('Account created successfully!');
      navigate('/profile');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Tab switching ───────────────────────────────────────────────────────
  const switchTo = (next) => {
    setMode(next);
    setErrors({});
    if (next === 'login') {
      // Reset to default method + email step when returning to login
      setLoginMethod('password');
      setPassword('');
      setShowPassword(false);
      setStep('email');
      setOtpCode('');
      clearCountdown();
      setSecondsLeft(0);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Sign In — VietClinics (Registered Patients)</title>
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      <Header />

      <div className="container mx-auto px-4 py-16">
        <div className="max-w-md mx-auto">
          <p className="text-center text-xs text-gray-500 mb-6" data-testid="login-registered-note">
            For existing registered patients. New here?{' '}
            <Link to="/patient-support-vietnam" className="text-[#1FA7A1] font-semibold hover:underline">Explore patient services</Link>.
          </p>
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#1FA7A1]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <User className="h-8 w-8 text-[#1FA7A1]" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {mode === 'login' ? 'Welcome to VietClinics' : 'Create Your Account'}
            </h1>
            <p className="text-gray-600">
              {mode === 'login'
                ? t('patientAuth.loginSubtitle')
                : 'Join thousands of patients finding great healthcare'}
            </p>
          </div>

          {/* Tab Toggle */}
          <div className="flex bg-gray-100 rounded-xl p-1 mb-6">
            <button
              onClick={() => switchTo('login')}
              className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all ${
                mode === 'login' ? 'bg-white text-[#1FA7A1] shadow-sm' : 'text-gray-500'
              }`}
              data-testid="login-tab"
            >
              Sign In
            </button>
            <button
              onClick={() => switchTo('register')}
              className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all ${
                mode === 'register' ? 'bg-white text-[#1FA7A1] shadow-sm' : 'text-gray-500'
              }`}
              data-testid="register-tab"
            >
              Create Account
            </button>
          </div>

          {/* ─── Login tab (Password + OTP) ─── */}
          {mode === 'login' && (
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              {/* Method switcher */}
              <div className="flex bg-gray-100 rounded-xl p-1 mb-4" role="tablist" aria-label="Sign-in method">
                <button
                  type="button"
                  role="tab"
                  aria-selected={loginMethod === 'password'}
                  onClick={() => switchLoginMethod('password')}
                  className={`flex-1 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                    loginMethod === 'password' ? 'bg-white text-[#1FA7A1] shadow-sm' : 'text-gray-500'
                  }`}
                  data-testid="login-method-password"
                >
                  {t('patientAuth.methodPassword')}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={loginMethod === 'otp'}
                  onClick={() => switchLoginMethod('otp')}
                  className={`flex-1 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                    loginMethod === 'otp' ? 'bg-white text-[#1FA7A1] shadow-sm' : 'text-gray-500'
                  }`}
                  data-testid="login-method-otp"
                >
                  {t('patientAuth.methodOtp')}
                </button>
              </div>
              <p className="text-xs text-gray-500 mb-4 text-center" data-testid="login-method-hint">
                {loginMethod === 'password' ? t('patientAuth.methodHintPassword') : t('patientAuth.methodHintOtp')}
              </p>

              {/* ─── Password method ─── */}
              {loginMethod === 'password' && (
                <form onSubmit={handlePasswordLogin} className="space-y-4" data-testid="password-login-form">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <Input
                        type="email"
                        placeholder="your@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className={`pl-10 h-12 ${errors.email ? 'border-red-500' : ''}`}
                        data-testid="email-input"
                        autoComplete="email"
                        autoFocus
                      />
                    </div>
                    {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className={`pl-10 pr-10 h-12 ${errors.password ? 'border-red-500' : ''}`}
                        data-testid="password-input"
                        autoComplete="current-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                    {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
                  </div>

                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-[#1FA7A1] hover:bg-[#178f8a] h-12 text-base font-semibold"
                    data-testid="password-submit-btn"
                  >
                    {isLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                        Signing in...
                      </>
                    ) : (
                      <>
                        Sign In
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </>
                    )}
                  </Button>
                </form>
              )}

              {/* ─── OTP method ─── */}
              {loginMethod === 'otp' && step === 'email' && (
                <form onSubmit={handleRequestOtp} className="space-y-4" data-testid="otp-email-form">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <Input
                        type="email"
                        placeholder="your@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className={`pl-10 h-12 ${errors.email ? 'border-red-500' : ''}`}
                        data-testid="email-input"
                        autoComplete="email"
                        autoFocus
                      />
                    </div>
                    {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                  </div>

                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-[#1FA7A1] hover:bg-[#178f8a] h-12 text-base font-semibold"
                    data-testid="send-code-btn"
                  >
                    {isLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                        {t('patientAuth.sendCode')}
                      </>
                    ) : (
                      <>
                        {t('patientAuth.sendCode')}
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </>
                    )}
                  </Button>
                </form>
              )}

              {loginMethod === 'otp' && step === 'code' && (
                <form onSubmit={handleVerifySubmit} className="space-y-4" data-testid="otp-code-form">
                  {/* Email display + change link */}
                  <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-xl px-4 py-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <Mail className="h-4 w-4 text-gray-400 shrink-0" />
                      <span className="text-sm text-gray-700 truncate" data-testid="otp-email-display">
                        {email}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={handleChangeEmail}
                      className="text-sm font-medium text-[#1FA7A1] hover:underline shrink-0 ml-3"
                      data-testid="change-email-btn"
                    >
                      {t('patientAuth.changeEmail')}
                    </button>
                  </div>

                  {/* Code input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('patientAuth.codeLabel')}
                    </label>
                    <div className="relative">
                      <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <Input
                        ref={codeInputRef}
                        type="text"
                        inputMode="numeric"
                        pattern="\d{6}"
                        maxLength={6}
                        autoComplete="one-time-code"
                        placeholder="••••••"
                        value={otpCode}
                        onChange={(e) => handleCodeChange(e.target.value)}
                        className={`pl-10 h-14 text-center text-2xl font-semibold tracking-[0.5em] ${errors.code ? 'border-red-500' : ''}`}
                        data-testid="otp-code-input"
                        autoFocus
                      />
                    </div>
                    {errors.code && <p className="text-red-500 text-xs mt-1">{errors.code}</p>}
                    <p className="text-xs text-gray-500 mt-2 text-center">
                      {t('patientAuth.codeSent')}
                    </p>
                  </div>

                  {/* Verify button */}
                  <Button
                    type="submit"
                    disabled={isLoading || otpCode.length !== 6}
                    className="w-full bg-[#1FA7A1] hover:bg-[#178f8a] h-12 text-base font-semibold"
                    data-testid="verify-code-btn"
                  >
                    {isLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                        {t('patientAuth.verifyAndSignIn')}
                      </>
                    ) : (
                      <>
                        {t('patientAuth.verifyAndSignIn')}
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </>
                    )}
                  </Button>

                  {/* Resend */}
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={handleResend}
                      disabled={secondsLeft > 0 || isLoading}
                      className="text-sm font-medium text-[#1FA7A1] hover:underline disabled:text-gray-400 disabled:no-underline disabled:cursor-not-allowed"
                      data-testid="resend-code-btn"
                    >
                      {secondsLeft > 0
                        ? t('patientAuth.resendCodeIn', { seconds: formatMMSS(secondsLeft) })
                        : t('patientAuth.resendCode')}
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          {/* ─── Register tab (unchanged behaviour: email + password + name + phone) ─── */}
          {mode === 'register' && (
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      type="text"
                      placeholder="John Doe"
                      value={regForm.name}
                      onChange={(e) => setRegForm({ ...regForm, name: e.target.value })}
                      className={`pl-10 h-12 ${errors.name ? 'border-red-500' : ''}`}
                      data-testid="name-input"
                    />
                  </div>
                  {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      type="email"
                      placeholder="your@email.com"
                      value={regForm.email}
                      onChange={(e) => setRegForm({ ...regForm, email: e.target.value })}
                      className={`pl-10 h-12 ${errors.email ? 'border-red-500' : ''}`}
                      data-testid="register-email-input"
                    />
                  </div>
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone <span className="text-gray-400">(optional)</span>
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      type="tel"
                      placeholder="+84 xxx xxx xxx"
                      value={regForm.phone}
                      onChange={(e) => setRegForm({ ...regForm, phone: e.target.value })}
                      className={`pl-10 h-12 ${errors.phone ? 'border-red-500' : ''}`}
                      data-testid="phone-input"
                    />
                  </div>
                  {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Min. 8 characters"
                      value={regForm.password}
                      onChange={(e) => setRegForm({ ...regForm, password: e.target.value })}
                      className={`pl-10 pr-10 h-12 ${errors.password ? 'border-red-500' : ''}`}
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
                </div>

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-[#1FA7A1] hover:bg-[#178f8a] h-12 text-base font-semibold"
                  data-testid="auth-submit-btn"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                      Creating account...
                    </>
                  ) : (
                    <>
                      Create Account
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </>
                  )}
                </Button>
              </form>
            </div>
          )}

          {/* Trust Indicators */}
          <div className="mt-6 flex items-center justify-center gap-2 text-sm text-gray-500">
            <Shield className="h-4 w-4" />
            <span>Your data is secure and encrypted</span>
          </div>

          <p className="mt-4 text-xs text-center text-gray-500">
            By continuing, you agree to our{' '}
            <Link to="/terms" className="text-[#1FA7A1] hover:underline">Terms of Service</Link>
            {' '}and{' '}
            <Link to="/privacy" className="text-[#1FA7A1] hover:underline">Privacy Policy</Link>
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PatientLoginPage;
